/**
 * PipelineProgress — Renders agent pipeline step progress during pre-stream phase.
 *
 * Shows ✅ done / 🔄 active / ○ pending step indicators matching backend's
 * ThinkingStatus phase/step metadata.
 */

import { memo } from 'react';
import { motion } from 'framer-motion';
import { computeStepStates } from './types';
import type { ThinkingProgress } from '../../../features/chat/api';

import styles from './PipelineProgress.module.css';

interface PipelineProgressProps {
  thinking: ThinkingProgress;
}

export const PipelineProgress = memo(function PipelineProgress({
  thinking,
}: PipelineProgressProps) {
  const steps = computeStepStates(thinking.phase, thinking.step);

  if (steps.length === 0) {
    return (
      <span className={styles.fallback}>
        <motion.span
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: [0.2, 0.8, 0.2, 1] }}
        >
          {thinking.status}
        </motion.span>
      </span>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.statusLine}>
        <motion.span
          className={styles.statusText}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2, ease: [0.2, 0.8, 0.2, 1] }}
        >
          {thinking.status}
        </motion.span>
      </div>
      <div className={styles.steps}>
        {steps.map((s) => (
          <div key={s.label} className={styles.step} data-state={s.state}>
            <span className={styles.icon}>
              {s.state === 'done' ? '✓' : s.state === 'active' ? '▸' : ''}
            </span>
            <span className={styles.label}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
});
